import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { environment } from '@environments/environment'
import { DCTSetup } from '../types/dct-setup.type'
import { DTCMSetup } from '../types/dtcm-setup.type'
import { map, Observable } from 'rxjs'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { DCTSettings } from '@app/modules/personas/interfaces/dct-settings.interface'

// Map of legal service types to their setup types
type LegalServiceTypesMap = {
  DCT: DCTSetup
  DTCM: DTCMSetup
}

@Injectable()
export class EventLegalService {
  private readonly _checkedSectionsKey = 'checked-sections'

  constructor(private _http: HttpClient) {}

  /**
   * Validate DTCM section
   * @param form FormGroup
   * @returns Observable<Response<void>>
   */
  public validateDTCMSection(form: UntypedFormGroup): Observable<Response<void>> {
    return this._http.post<Response<void>>(`${environment.registrationsApi}dtcm/test-config`, {
      eventCode: (form.value.eventCode as string).toUpperCase(),
      section: (form.value.section as string).toUpperCase(),
      priceTypeCode: (form.value.priceTypeCode as string)?.toUpperCase(),
      priceTypePrice: form.value.priceTypePrice ? form.value.priceTypePrice.toString() : '0',
      sectionId: form.value.sectionId,
      sellerCode: form.value.sellerCode,
      email: form.value.email
    })
  }

  /**
   * Validate DCT product
   * @param {DCTSettings} payload
   * @returns Observable<Response<void>>
   */
  public validateDCTProduct(payload: DCTSettings): Observable<Response<void>> {
    return this._http.post<Response<void>>(`${environment.registrationsApi}dct/test-config`, payload)
  }

  /**
   * Gets sections and prices
   * @param eventCode string
   * @returns Observable<Response<DTCMSetup>>
   */
  public getDtcmDetails(eventCode: string): Observable<Response<DTCMSetup>> {
    return this._http.get<Response<DTCMSetup>>(`${environment.registrationsApi}dtcm/map/${eventCode}`)
  }

  /**
   * Gets the setup details for the given service type and event id
   * @param {'DCT' | 'DTCM'} serviceType
   * @param {string} eventId
   * @returns Observable<Response<DCTSetup | DTCMSetup>>
   */
  public getSetupDetails<T extends keyof LegalServiceTypesMap>(serviceType: T, eventId: string): Observable<LegalServiceTypesMap[T]> {
    return this._http.get<Response<LegalServiceTypesMap[T]>>(`${environment.registrationsApi}${serviceType.toLowerCase()}/map/${eventId}`).pipe(map((res) => res.data))
  }

  /**
   * Local cached DTCM "checked sections".
   * Components should not read localStorage directly.
   */
  public getCheckedSections(): any[] {
    try {
      const raw = localStorage.getItem(this._checkedSectionsKey)
      const parsed = raw ? JSON.parse(raw) : []
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  public setCheckedSections(sections: any[]): void {
    localStorage.setItem(this._checkedSectionsKey, JSON.stringify(sections ?? []))
  }

  public removeCheckedSectionByPersonaId(personaId: number): void {
    const next = this.getCheckedSections().filter((s) => s?.personaId != personaId)
    this.setCheckedSections(next)
  }
}
